import { CartAddEvent, ThemeEvents } from '@theme/events';

class CroPdpSticky extends HTMLElement {
  #section = null;
  #target = null;
  #button = null;
  #observer = null;
  #submitted = false;

  connectedCallback() {
    this.#section = this.closest('.shopify-section');
    this.#target = this.#section?.querySelector(this.dataset.target);
    this.#button = this.querySelector('[data-cro-sticky-atc]');
    if (!this.#target || !this.#button) {
      document.body.removeAttribute('data-sticky-hidden');
      return;
    }

    if (this.#button.type === 'submit') {
      const formId = this.#target.form?.getAttribute('id');
      if (!formId) {
        document.body.removeAttribute('data-sticky-hidden');
        return;
      }
      this.#button.setAttribute('form', formId);
      this.#section.addEventListener('change', this.#onChange);
      this.#section.addEventListener('variant:update', this.#onVariantUpdate);
      window.addEventListener('submit', this.#onSubmit, true);
      this.#section.addEventListener(CartAddEvent.eventName, this.#onCartAdd);
      window.addEventListener(ThemeEvents.cartError, this.#onCartError);
      this.#sync();
    } else {
      this.#button.addEventListener('click', this.#scrollToTarget);
    }

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        const visible = !entry.isIntersecting;
        this.toggleAttribute('data-visible', visible);
        document.body.toggleAttribute('data-sticky-visible', visible);
        document.body.toggleAttribute('data-sticky-hidden', !visible);
      },
      { rootMargin: '0px 0px 100000px' }
    );
    this.#observer.observe(this.#target);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    document.body.removeAttribute('data-sticky-visible');
    document.body.removeAttribute('data-sticky-hidden');
    this.#section?.removeEventListener('change', this.#onChange);
    this.#section?.removeEventListener('variant:update', this.#onVariantUpdate);
    window.removeEventListener('submit', this.#onSubmit, true);
    this.#section?.removeEventListener(CartAddEvent.eventName, this.#onCartAdd);
    window.removeEventListener(ThemeEvents.cartError, this.#onCartError);
  }

  #onSubmit = (event) => {
    if (this.#section.contains(event.target)) this.#submitted = event.submitter === this.#button;
  };

  #onCartAdd = () => {
    this.#submitted = false;
  };

  #onCartError = () => {
    if (!this.#submitted) return;
    this.#submitted = false;
    if (this.hasAttribute('data-visible')) this.#scrollToTarget();
  };

  #scrollToTarget = () => {
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!this.#target.hasAttribute('tabindex')) this.#target.tabIndex = -1;
    this.#target.focus({ preventScroll: true });
    this.#target.scrollIntoView({ behavior: reduce ? 'instant' : 'smooth', block: 'center' });
  };

  #onChange = () => requestAnimationFrame(this.#sync);

  #onVariantUpdate = ({ detail: { resource } }) => {
    if (resource) this.#setTotal(this.dataset.moneyFormat.replace(/\{\{\s*amount\s*\}\}/, (resource.price / 100).toFixed(2)));
    const available = Boolean(resource?.available);
    this.#button.disabled = !available;
    this.querySelector('[data-sticky-add]').hidden = !available;
    this.querySelector('[data-sticky-sold-out]').hidden = available;
    this.#onChange();
  };

  #setTotal(total) {
    if (total) for (const el of this.querySelectorAll('[data-sticky-total]')) el.textContent = total;
  }

  #sync = () => {
    if (this.dataset.price) this.#setTotal(this.#section.querySelector(this.dataset.price)?.textContent.trim());

    const choice = this.#section.querySelector('input:checked[data-cro-summary]');
    const options = [...this.#section.querySelectorAll('variant-picker input:checked, variant-picker select')].map((el) => el.value);
    const summary = choice?.dataset.croSummary ?? options.join(' / ');
    if (!summary) return;
    this.querySelector('[data-sticky-summary]').textContent = summary;
    this.querySelector('[data-sticky-summary-short]').textContent = choice?.dataset.croSummaryShort ?? summary;
  };
}

if (!customElements.get('cro-pdp-sticky')) customElements.define('cro-pdp-sticky', CroPdpSticky);
